import React, { useState, useMemo } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { Avatar } from "../shared/Avatar";
import { apiCreateGiving, apiUpdateGiving } from "./api";
import { todayLocalISO } from "../../lib/localDate";

const GIVING_CATEGORIES = ["Regular Offering", "Tithe", "Seed", "Alms", "Other"];
const PAYMENT_METHODS = ["Cash", "Cheque", "E-Transfer", "Card"];

function todayISO() {
  return todayLocalISO();
}

function dollarsToCents(v) {
  const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

export default function GivingEntryModal({ open, members, editRecord, onClose, onSaved, currencySymbol = "$" }) {
  const isEdit = Boolean(editRecord);
  const [isAnonymous, setIsAnonymous] = useState(editRecord?.isAnonymous || false);
  const [memberId, setMemberId] = useState(editRecord?.memberId || "");
  const [memberSearch, setMemberSearch] = useState("");
  const [date, setDate] = useState(editRecord?.date || todayISO());
  const [amountDollars, setAmountDollars] = useState(
    editRecord ? (editRecord.amountCents / 100).toFixed(2) : ""
  );
  const [category, setCategory] = useState(editRecord?.category || "Tithe");
  const [categoryOther, setCategoryOther] = useState(editRecord?.categoryOther || "");
  const [paymentMethod, setPaymentMethod] = useState(editRecord?.paymentMethod || "E-Transfer");
  const [notes, setNotes] = useState(editRecord?.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const filteredMembers = useMemo(() => {
    if (isAnonymous) return [];
    const q = memberSearch.trim().toLowerCase();
    if (!q) return (members || []).slice(0, 8);
    return (members || [])
      .filter(m => `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [members, memberSearch, isAnonymous]);

  const selectedMember = useMemo(
    () => (members || []).find(m => m.id === memberId),
    [members, memberId]
  );

  if (!open) return null;

  const submit = async () => {
    setError("");
    if (!isAnonymous && !memberId) {
      setError("Pick a member or check Anonymous Giver.");
      return;
    }
    const amountCents = dollarsToCents(amountDollars);
    if (amountCents <= 0) {
      setError("Amount must be greater than 0.");
      return;
    }
    if (category === "Other" && !categoryOther.trim()) {
      setError("Please specify the category.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        memberId: isAnonymous ? null : memberId,
        isAnonymous,
        date,
        amountCents,
        category,
        categoryOther: category === "Other" ? categoryOther.trim() : null,
        paymentMethod: paymentMethod || null,
        notes: notes.trim() || null,
      };
      if (isEdit) {
        const updated = await apiUpdateGiving(editRecord.recordId, payload);
        onSaved(updated);
      } else {
        const created = await apiCreateGiving(payload);
        onSaved(created);
      }
      onClose();
    } catch (e) {
      setError(e.message || "Could not save giving record.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="giving-entry-modal">
      <div className="modal" style={{ maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CustomIcon name="coin" size={22} />
            <h2 style={{ margin: 0 }}>{isEdit ? "Edit Giving Record" : "Log Giving"}</h2>
          </div>
          <button className="modal-close" onClick={onClose} data-testid="giving-modal-close">×</button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => { setIsAnonymous(e.target.checked); if (e.target.checked) setMemberId(""); }}
              data-testid="giving-anonymous-checkbox"
            />
            <span>Anonymous Giver</span>
          </label>

          {!isAnonymous && (
            <div>
              <label className="form-label">Member</label>
              {selectedMember ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", borderRadius: 8 }}>
                  <Avatar photo={selectedMember.photo} name={`${selectedMember.firstName} ${selectedMember.lastName}`} memberId={selectedMember.id} status={selectedMember.memberStatus} size={32} />
                  <div style={{ flex: 1 }}>{selectedMember.firstName} {selectedMember.lastName}</div>
                  <button className="btn btn-outline btn-sm" onClick={() => setMemberId("")} data-testid="giving-clear-member">Change</button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search by name…"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    data-testid="giving-member-search"
                  />
                  {filteredMembers.length > 0 && (
                    <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)", borderRadius: 8, marginTop: 4 }}>
                      {filteredMembers.map(m => (
                        <div
                          key={m.id}
                          onClick={() => { setMemberId(m.id); setMemberSearch(""); }}
                          style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, cursor: "pointer", borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 10%, transparent)" }}
                          data-testid={`giving-member-result-${m.id}`}
                        >
                          <Avatar photo={m.photo} name={`${m.firstName} ${m.lastName}`} memberId={m.id} status={m.memberStatus} size={28} />
                          <span>{m.firstName} {m.lastName}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <label className="form-label">Date</label>
            <input type="date" className="form-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="giving-date" />
          </div>

          <div>
            <label className="form-label">Amount ({currencySymbol})</label>
            <input
              type="text"
              inputMode="decimal"
              className="form-input"
              placeholder="0.00"
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              data-testid="giving-amount"
            />
          </div>

          <div>
            <label className="form-label">Category</label>
            <select className="form-input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="giving-category">
              {GIVING_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {category === "Other" && (
              <input
                type="text"
                className="form-input"
                placeholder="e.g. Rent, Mission, Building Fund"
                value={categoryOther}
                onChange={(e) => setCategoryOther(e.target.value)}
                style={{ marginTop: 6 }}
                data-testid="giving-category-other"
              />
            )}
          </div>

          <div>
            <label className="form-label">Payment Method (optional)</label>
            <select className="form-input" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} data-testid="giving-payment-method">
              <option value="">— None —</option>
              {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div>
            <label className="form-label">Notes (optional)</label>
            <textarea className="form-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="giving-notes" />
          </div>

          {error && <div className="alert-error" data-testid="giving-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving} data-testid="giving-save-btn">
            {saving ? "Saving…" : (isEdit ? "Save Changes" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
}
